import React from "react";
import { useController } from "react-hook-form";

import { AnswerProps, AnswerType, QuestionProps } from "./types";
import * as Input from "components/Inputs/Input";
import TextArea from "components/Inputs/TextArea";
import Checkbox from "components/Inputs/Checkbox";
import Select from "components/Inputs/Select";

import * as styles from "./styles.module.scss";

function Answer<T>({
  children,
  disabled,
  field: { name, onBlur, onChange, ref, value },
  invalid,
  isSubmitting,
  label,
  options,
  rows,
  type,
}: AnswerProps<T>) {
  switch (type) {
    case AnswerType.TEXT:
      return (
        <Input.String
          forwardedRef={ref}
          invalid={invalid}
          label={label}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          readOnly={isSubmitting}
          tabIndex={disabled ? -1 : 0}
          value={String(value)}
        />
      );
    case AnswerType.TEXTAREA:
      return (
        <TextArea
          label={label}
          forwardedRef={ref}
          invalid={invalid}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          readOnly={isSubmitting}
          tabIndex={disabled ? -1 : 0}
          rows={rows}
          value={String(value)}
        />
      );
    case AnswerType.CHECKBOX:
      const handleMouseDown = () => {
        onChange(value === true ? false : true);
      };
      const handleKeyDown = (event: React.KeyboardEvent<HTMLLabelElement>) => {
        if (event.key === "Enter") {
          onChange(value === true ? false : true);
          event.stopPropagation();
        }
      };
      return (
        <Checkbox
          checked={value === true ? true : false}
          forwardedRef={ref}
          invalid={invalid}
          label={label}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          onLabelClick={handleMouseDown}
          onLabelKeyDown={handleKeyDown}
          readOnly={isSubmitting}
          tabIndex={disabled ? -1 : 0}
          value={String(value)}
        >
          {children}
        </Checkbox>
      );
    case AnswerType.SELECT:
      return (
        <Select
          disabled={(options && options.length === 0) || options === undefined}
          forwardedRef={ref}
          invalid={invalid}
          label={label}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          options={options}
          readOnly={isSubmitting}
          tabIndex={disabled ? -1 : 0}
          value={String(value)}
        >
          {children}
        </Select>
      );
    default:
      return null;
  }
}

function Component<T>({
  children,
  control,
  disabled,
  isSubmitting,
  label,
  question: { name },
  options,
  rows,
  type = AnswerType.TEXT,
}: QuestionProps<T>) {
  const {
    field: { onChange, value, ...field },
    fieldState: { invalid, error },
  } = useController({
    control,
    name: name,
  });
  return (
    <div className={styles.question}>
      <Answer<T>
        disabled={disabled}
        field={{ onChange, value, ...field }}
        invalid={invalid}
        isSubmitting={isSubmitting}
        label={label}
        options={options}
        rows={rows}
        type={type}
      >
        {children}
      </Answer>
      {invalid && <p>{(error as any)?.message}</p>}
    </div>
  );
}

export default React.memo(Component) as typeof Component;
